import { BodyText, Heading, Icon, IconSet } from '@postscript/components';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';

import { WorkflowAside } from 'components/ProgressiveOnboarding/WorkflowAside';
import { WorkflowStep } from 'components/ProgressiveOnboarding/WorkflowStep';

import type { Guide } from 'providers/ProgressiveOnboarding/types';

import classes from 'components/ProgressiveOnboarding/OnboardingWorkflow.module.css';

export const OnboardingWorkflow = ({ guide }: { guide: Guide }) => {
  const { title, subtitle, icon, steps, callout } = guide;

  const [openStep, setOpenStep] = useState<string | null>(
    steps.find((step) => step.state === 'incomplete')?.key ?? null,
  );

  const totalSteps = steps.length;
  const completedSteps = steps.filter(
    (step) => step.state === 'complete',
  ).length;
  const progressPercentage = (completedSteps / totalSteps) * 100;

  const toggleStep = (key: string) => {
    if (openStep === key) {
      setOpenStep(null);
    } else {
      setOpenStep(key);
    }
  };

  return (
    <div className={classes.onboardingWorkflow}>
      <div className={classes.header}>
        <div className={classes.icon}>
          <Icon component={IconSet[icon]} size="large" />
        </div>

        <div className={classes.title}>
          <Dialog.Title asChild>
            <Heading size="small">{title}</Heading>
          </Dialog.Title>

          {subtitle && (
            <Dialog.Description asChild>
              <BodyText>{subtitle}</BodyText>
            </Dialog.Description>
          )}
        </div>
      </div>

      <div className={classes.progress}>
        <BodyText>
          {completedSteps}/{totalSteps}
        </BodyText>

        <div className={classes.progressBar}>
          <div
            className={classes.indicator}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {callout && <WorkflowAside {...callout} />}

      <div className={classes.content}>
        {steps.map((step) => (
          <WorkflowStep
            {...step}
            key={step.key}
            isOpen={step.key === openStep}
            toggleStep={() => toggleStep(step.key)}
            primaryAction={
              step.PrimaryAction ? (
                <step.PrimaryAction step={step} />
              ) : undefined
            }
            secondaryAction={
              step.SecondaryAction ? (
                <step.SecondaryAction step={step} />
              ) : undefined
            }
            nodeAbove={step.nodeAbove}
            tooltip={step.tooltip}
          />
        ))}
      </div>
    </div>
  );
};
